import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { qk } from '@/lib/query-client'
import { useWorkspace } from '@/providers/workspace-provider'
import type { UserRole } from '@/types/database'

export function useCreateWorkspace() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      name,
      slug,
      description,
    }: {
      name: string
      slug: string
      description?: string
    }) => {
      const { data, error } = await supabase.rpc('create_workspace', {
        p_name: name,
        p_slug: slug,
        p_description: description ?? null,
      })
      if (error) throw error
      return data as string
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.workspaces }),
  })
}

export function useUpdateMemberRole() {
  const { workspaceId } = useWorkspace()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: UserRole }) => {
      if (!workspaceId) throw new Error('Nenhum workspace selecionado')
      const { error } = await supabase
        .from('workspace_members')
        .update({ role })
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(workspaceId ?? 'none') }),
  })
}

export function useRemoveMember() {
  const { workspaceId } = useWorkspace()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string) => {
      if (!workspaceId) throw new Error('Nenhum workspace selecionado')
      const { error } = await supabase
        .from('workspace_members')
        .delete()
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(workspaceId ?? 'none') }),
  })
}
